<template>
  <component
    v-bind="attrs"
    :is="componentType"
    ref="inputRef"
    :value="modelValue"
    :placeholder="placeholder"
    :class="[
      bootstapResets,
      baseClasses,
      backgroundClasses,
      borderClasses,
      textClasses,
      extraClasses,
    ]"
    class="sf-input-base"
    :disabled="disabled"
  />
</template>
<script setup lang="ts" generic="MV">
import { useFocus } from '@vueuse/core'
import { computed, ref, toRef, useAttrs, watch } from 'vue'

import useError from './use-error'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SfInputBase',
  inheritAttrs: false,
})

interface Props {
  align?: 'left' | 'center' | 'right'
  componentType?: 'input' | 'textarea'
  focused?: boolean | number
  hasError?: boolean
  hideBrowserAppearance?: boolean
  disabled?: boolean
  errorText?: string | string[]
  helperText?: string
  placeholder?: string
  small?: boolean
  modelValue?: MV
  rightPadding?: `${string}px` | ''
  squareBottom?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  align: 'left',
  componentType: 'input',
  focused: 0,
  errorText: () => [],
  helperText: '',
  placeholder: '',
  modelValue: undefined,
  rightPadding: '8px',
})

const $attrs = useAttrs()

const emit =
  defineEmits<{
    'update:modelValue': [value: string | null]
  }>()

const inputRef = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)

const { focused: vFocused } = useFocus(inputRef, {
  initialValue: !!props.focused,
})

const errors = useError(toRef(props, 'errorText'))

const bootstapResets = computed(() => {
  return {
    '!tw-h-auto': props.componentType !== 'textarea',
    'tw-m-0': true,
    '!tw-mb-0': true,
    '!tw-shadow-none': true,
    '!tw-outline-none': true,
    'focus:!tw-outline-none': true,
    'focus:!tw-shadow-none': true,
    'active:!tw-outline-none': true,
    'active:!tw-shadow-none': true,
  }
})

const baseClasses = ['tw-p-[7px]', '!tw-leading-4', 'tw-w-full']

const backgroundClasses = computed(() => {
  return {
    '!tw-bg-sf-white': !props.disabled,
    '!tw-bg-sf-neutral-subtle-1': props.disabled,
  }
})

const borderClasses = computed(() => {
  return {
    'tw-rounded': !props.small,
    'tw-rounded-sm': props.small,
    'tw-rounded-b-none': props.squareBottom,
    'tw-border': true,
    'tw-border-solid': true,
    '!tw-border-sf-snow-darker': !props.hasError,
    '!tw-border-sf-danger': props.hasError,
    'hover:!tw-border-sf-gray-light': !props.disabled,
    'focus:!tw-border-sf-primary': !props.disabled,
    'active:!tw-border-sf-primary': !props.disabled,
  }
})

const textClasses = computed(() => {
  return {
    'tw-font-sans': true,
    'tw-font-normal': true,
    '!tw-text-sm': true,
    '!tw-text-sf-gray-darker': !props.disabled,
    '!tw-text-sf-gray-lighter': props.disabled,
    'placeholder:tw-text-sf-gray-light': true,
    'placeholder:tw-italic': true,
  }
})

const extraClasses = computed(() => {
  return {
    'sf-input-danger': errors.value.length > 0 || props.hasError,
    'sf-input-small': props.small,
    'sf-input-base--no-appearance': props.hideBrowserAppearance,
    'tw-text-left': props.align === 'left',
    'tw-text-center': props.align === 'center',
    'tw-text-right': props.align === 'right',
    'tw-cursor-not-allowed': props.disabled,
  }
})

const attrs = computed(() => {
  return {
    ...$attrs,
    type: $attrs.type || 'text',
    onInput: (event: Event) => {
      const target = event.target as HTMLInputElement
      emit('update:modelValue', target.value)
    },
  }
})

watch(
  () => props.focused,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      vFocused.value = !!newValue
    }
  }
)
</script>

<style scoped lang="scss">
.sf-input-base {
  padding-right: v-bind(rightPadding);

  &--no-appearance {
    appearance: none;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      appearance: none;
      margin: 0;
    }
  }
}
</style>
